import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Panel, Form, FormControl, FormGroup, Col, ControlLabel, InputGroup } from 'react-bootstrap';
import { bind } from '../../utils/decorators';
import * as ApiTypes from '../../models/ApiTypes';
import ObjektMessmittelUebernehmenModal from './ObjektMessmittelUebernehmen';
import ObjektAnlegen, { ObjektAnlegenHeader } from './ObjektAnlegen';
import { round } from '../../utils/round';

/** Formular zum Anlegen eines neuen Druckmessgerätes (Kurz DMG) */
@observer
export default class ObjektAnlegenDMG extends ObjektAnlegen {

  @observable objekt: Partial<ApiTypes.IObjekt> = {
    objektid: '',
    hersteller: '',
    name: '',
    sernr: '',
    typbezeichnung: '',
    typ: 'DMG',
    bemerkung: ''
  };

  @observable objektMeta = {
    anzeige: '',
    messbereichvon: '',
    messbereichbis: '',
    genauigkeitsklasse: '',
    maxfehlergrenze: '',
    skalenteilung: '',
    ablesegenauigkeit: '',
    einbaulage: '',
    bezugsebene: '',
    sernrbps: ''
  };

  async componentDidMount() {
    // @todo: Abklären ob Vorlagen genutzt werden sollen/können
    const objektVorlagen = await this.apiStore.objektVorlagen('dmg');
    if (objektVorlagen.ok) {
      this.setObjektVorlagen(objektVorlagen.json);
    }
  }

  @bind async handleObjektAnlegenSubmit(event: React.FormEvent<FormControl>) {
    event.preventDefault();

    const objekt = Object.assign({}, this.objekt);
    objekt.meta = [];
    for (const key of Object.keys(this.objektMeta)) {
      objekt.meta.push({ bezeichnung: key, inhalt: this.objektMeta[key] });
    }
    console.log(objekt);
    if (this.props.onAddObjekt) {
      this.props.onAddObjekt(objekt);
    }
  }

  /** Übernimmt die Daten aus der gewählten Vorlage in das anzulegende Objekt */
  @bind @action uebernehmeObjektEigenschaften() {
    const vorlage = this.vorlageZuModell(this.objekt.hersteller, this.objekt.typbezeichnung);
    if (vorlage) {
      this.objekt.art = vorlage.art;
      if (vorlage.arttext) {
        this.objekt.name = vorlage.arttext;
      }
    } else {
      this.objekt.art = '';
      this.objekt.name = '';
      // @todo
    }
  }

  /** Übernimmt die Daten aus einem Messmittel der ÜO, soweit diese im ÜO Bereich vorhanden sind */
  @bind @action uebernehmeMessmittelDaten(messmittel: ApiTypes.IMessmittelUO) {
    this.objekt.objektid = messmittel.pmnummer;
    this.objekt.messmittelkennung = messmittel.pmnummer;
    const vorlageHersteller = this.objektVorlagen.find(el => el.hersteller.toLocaleLowerCase() === messmittel.hersteller.toLocaleLowerCase());
    if (vorlageHersteller) {
      this.objekt.hersteller = vorlageHersteller.hersteller;
    }
    this.objekt.sernr = messmittel.seriennummer;
    // Bei der Übernahme aus einem ÜO Messmittel passt der Modellbezeichner wegen der freien Eingabe u.U. nicht zur Vorlage
    this.objektVorlagenTypen = this.vorlagenZuHersteller(messmittel.hersteller);
    // Zum Messmittel passende Vorlage wählen, wegen freier Eingabemöglichkeit im ÜO Umfeld nicht auf die Schreibweise achten
    const vorlage = this.vorlageZuModell(messmittel.hersteller, messmittel.name);
    if (vorlage) {
      this.objekt.typbezeichnung = vorlage.modell;
      this.uebernehmeObjektEigenschaften();
    }
    // @todo: Nicht vorhandene Vorlage visualisieren
    this.hideModal();
  }

  @bind @action handleEditValueChanged(event: React.FormEvent<FormControl>) {
    const value = (event.target as HTMLInputElement).value;
    const id = (event.target as HTMLInputElement).id;

    if (id in this.objekt) {
      this.objekt[id] = value;
      // Beim Wechsel des Herstellers müssen bereits gewählte abhängige Daten gelöscht werden
      if (id === 'hersteller') {
        this.objekt.typbezeichnung = '';
        this.objekt.art = '';
        // @todo
        // Auswählbare Vorlagen auf die für den Hersteller verfügbaren einschränken
        this.objektVorlagenTypen = [];
        this.objektVorlagenTypen = this.objektVorlagen.filter(vorlage => vorlage.hersteller === this.objekt.hersteller);
      }
      // Beim Wechsel des Typs (aka Modell) die in der Objektvorlage hinterlegten Eigenschaften in das Formular übernehmen
      if (id === 'typbezeichnung') {
        this.uebernehmeObjektEigenschaften();
      }
      // Bei Druckaufnehmern wird keine Genauigkeitsklasse erfasst
      if ((id === 'name') && (['Druckaufnehmer', 'Funk-Druckaufnehmer'].indexOf(value) > -1)) {
        this.objektMeta.genauigkeitsklasse = '-';
        this.objektMeta.maxfehlergrenze = '0.1';
      }
      return;
    }

    if (id in this.objektMeta) {
      this.objektMeta[id] = value;
      if ((id === 'messbereichbis') || (id === 'genauigkeitsklasse')) {
        if (this.objektMeta.genauigkeitsklasse === '-') {
          // Bei Druckaufnehmern ist keine Genauigkeitsklasse anzugeben, dort wird die Fehlergrenze fest auf 0,1 gesetzt
          this.objektMeta.maxfehlergrenze = '0.1';
        } else {
          // Beim Setzen des Messbereichs kann die Fehlergrenze berechnet werden
          if ((!isNaN(parseFloat(this.objektMeta.messbereichvon))) && (!isNaN(parseFloat(this.objektMeta.messbereichbis)))  && (!isNaN(parseFloat(this.objektMeta.genauigkeitsklasse)))) {
            const delta = parseFloat(this.objektMeta.messbereichbis) - parseFloat(this.objektMeta.messbereichvon);
            const gk = parseFloat(this.objektMeta.genauigkeitsklasse);
            this.objektMeta.maxfehlergrenze = round(delta * (gk / 100.0), 3).toString();
          }
        }
      }
      // Ablesegenauigkeit aus Skalenteilung ermittel
      if ((id === 'skalenteilung') || (id === 'anzeige')) {
        if (this.objektMeta.anzeige.toLowerCase().indexOf('digital') > -1)  {
          this.objektMeta.ablesegenauigkeit = this.objektMeta.skalenteilung;
        } else {
          let floatVal = parseFloat(this.objektMeta.skalenteilung);
          if (floatVal) {
            this.objektMeta.ablesegenauigkeit = (floatVal / 5.0).toString();
          }
        }
      }
    }
  }

  validate() {
    // Alle Objekteigenschaften außer der Bemerkung und Art (bisher noch nicht vom DIQZ definiert) müssen ausgefüllt sein
    for (const key of Object.keys(this.objekt)) {
      if (((key !== 'bemerkung') && (key !== 'art') && (this.objekt[key] === ''))) {
        return false;
      }
    }
    // Alle Metaeigenschaften des Objekts müssen ausgefüllt sein
    for (const key of Object.keys(this.objektMeta)) {
      if ((key !== 'sernrbps')) {
        if (this.objektMeta[key] === '') {
          return false;
        }
      }
    }
    return true;
  }

  render() {
    return (
      <Form horizontal={true} onSubmit={this.handleObjektAnlegenSubmit}>
        <Panel className="panel-header-buttoned"
          // @todo: Übernahme aus Messmittel der ÜO bei DMG sinnvoll?
          header={<ObjektAnlegenHeader caption="Neues Druckmessgerät anlegen" showModal={this.showModal}/>}
          footer={(
            <div className="text-right">
              <Button onClick={() => this.uiStore.navigateBack()}>Abbrechen</Button>
              <Button bsStyle="primary" disabled={!this.validate()} type="submit">Anlegen</Button>
            </div>
          )}
        >
          <FormGroup validationState={this.objekt.hersteller === '' ? 'error' : undefined} controlId="hersteller">
            <Col componentClass={ControlLabel} sm={3}>Hersteller</Col>
            <Col sm={9}>
              <FormControl type="text" list="herstellerListe" value={this.objekt.hersteller} onChange={this.handleEditValueChanged}/>
              <datalist id="herstellerListe">
                {this.objektVorlagenHersteller.map((name) => <option key={name} value={name}>{name}</option>)}
              </datalist>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.typbezeichnung === '' ? 'error' : undefined} controlId="typbezeichnung">
            <Col componentClass={ControlLabel} sm={3}>Typ</Col>
            <Col sm={9}>
              <FormControl type="text" value={this.objekt.typbezeichnung} onChange={this.handleEditValueChanged} />
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.name === '' ? 'error' : undefined} controlId="name">
            <Col componentClass={ControlLabel} sm={3}>Kalibriergegenstand</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objekt.name} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  <option value="Manometer">Manometer</option>
                  <option value="Doppelmanometer">Doppelmanometer</option>
                  <option value="Bremsdruckprüfgerät">Bremsdruckprüfgerät</option>
                  <option value="Druckaufnehmer">Druckaufnehmer</option>
                  <option value="Funk-Druckaufnehmer">Funk-Druckaufnehmer</option>
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.sernr === '' ? 'error' : undefined} controlId="sernr">
            <Col sm={3} componentClass={ControlLabel}>Seriennr.</Col>
            <Col sm={9}>
              <FormControl type="text" label="Seriennr." id="sernr" value={this.objekt.sernr} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.objektid === '' ? 'error' : undefined} controlId="objektid">
            <Col sm={3} componentClass={ControlLabel}>Kennzeichnung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Kennzeichnung" id="objektid" value={this.objekt.objektid} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          {/* Anzeige  */}
          <FormGroup validationState={(this.objektMeta.anzeige === '' ) ? 'error' : undefined} controlId="anzeige">
            <Col sm={3}componentClass={ControlLabel}>Anzeige</Col>
            <Col sm={4}>
              <FormControl componentClass="select" placeholder="select" value={this.objektMeta.anzeige} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  <option value="digital">Digital</option>
                  <option value="analog">Analog</option>
                  <option value="strichskala">Strichskala</option>
                  <option value="digitalbps">Digital, Bremsprüfstand SN</option>
              </FormControl>
            </Col>
            {/* Je nach Auswahl Seriennr. des Bremsprüfstands erfassen lassen */}
            {this.objektMeta.anzeige === 'digitalbps' &&
              <Col sm={4}>
                <FormControl type="text" label="Sernrbps" id="sernrbps" placeholder="Sernr. des BPS" value={this.objektMeta.sernrbps} onChange={this.handleEditValueChanged}/>
              </Col>
            }
          </FormGroup>

          {/* Messbereich */}
          <FormGroup validationState={(this.objektMeta.messbereichvon === '' || this.objektMeta.messbereichbis === '') ? 'error' : undefined}>
            <Col sm={3}componentClass={ControlLabel}>Messbereich</Col>
            <Col sm={4}>
              <InputGroup>
                <InputGroup.Addon>Von</InputGroup.Addon>
                <FormControl type="number" step="1" id="messbereichvon" value={this.objektMeta.messbereichvon} onChange={this.handleEditValueChanged}/>
                <InputGroup.Addon>bar</InputGroup.Addon>
              </InputGroup>
            </Col>
            <Col sm={4}>
              <InputGroup>
                <InputGroup.Addon>Bis</InputGroup.Addon>
                <FormControl type="number" step="1" id="messbereichbis" value={this.objektMeta.messbereichbis} onChange={this.handleEditValueChanged}/>
                <InputGroup.Addon>bar</InputGroup.Addon>
              </InputGroup>
            </Col>
          </FormGroup>

          <FormGroup validationState={(this.objektMeta.genauigkeitsklasse === '' ) ? 'error' : undefined} controlId="genauigkeitsklasse">
            <Col sm={3}componentClass={ControlLabel}>Genauigkeitsklasse</Col>
            <Col sm={4}>
              <FormControl componentClass="select" placeholder="select" value={this.objektMeta.genauigkeitsklasse} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  <option value="0.1">0,1</option>
                  <option value="0.25">0,25</option>
                  <option value="0.6">0,6</option>
                  <option value="1.0">1,0</option>
                  <option value="1.6">1,6</option>
                  <option value="2.5">2,5</option>
                  <option value="4.0">4,0</option>
                  <option value="-">Nicht erforderlich</option>
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup validationState={(this.objektMeta.maxfehlergrenze === '' ) ? 'error' : undefined}>
            <Col sm={3}componentClass={ControlLabel}>Max. Fehlergrenze</Col>
            <Col sm={4}>
              <InputGroup>
                <FormControl type="number" step="any" id="maxfehlergrenze" value={this.objektMeta.maxfehlergrenze} onChange={this.handleEditValueChanged}/>
                <InputGroup.Addon>bar</InputGroup.Addon>
              </InputGroup>
            </Col>
          </FormGroup>

          <FormGroup validationState={(this.objektMeta.skalenteilung === '' ) ? 'error' : undefined}>
            <Col sm={3}componentClass={ControlLabel}>Skalenteilung</Col>
            <Col sm={4}>
              <InputGroup>
                <FormControl type="number" step="any" id="skalenteilung" value={this.objektMeta.skalenteilung} onChange={this.handleEditValueChanged}/>
                <InputGroup.Addon>bar</InputGroup.Addon>
              </InputGroup>
            </Col>
          </FormGroup>

          <FormGroup validationState={(this.objektMeta.ablesegenauigkeit === '' ) ? 'error' : undefined}>
            <Col sm={3}componentClass={ControlLabel}>Ablesegenauigkeit</Col>
            <Col sm={4}>
              <InputGroup>
                <FormControl type="number" step="any" readOnly={true} id="ablesegenauigkeit" value={this.objektMeta.ablesegenauigkeit} onChange={this.handleEditValueChanged}/>
                <InputGroup.Addon>bar</InputGroup.Addon>
              </InputGroup>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objektMeta.einbaulage === '' ? 'error' : undefined} controlId="einbaulage">
            <Col componentClass={ControlLabel} sm={3}>Einbaulage</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objektMeta.einbaulage} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  <option value="0">liegend (0°)</option>
                  <option value="90">senkrecht (90°)</option>
                  <option value="30">30°</option>
                  <option value="45">45°</option>
                  <option value="60">60°</option>
                  <option value="120">120°</option>
                  <option value="135">135°</option>
                  <option value="150">150°</option>
                  <option value="180">180°</option>
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objektMeta.bezugsebene === '' ? 'error' : undefined} controlId="bezugsebene">
            <Col componentClass={ControlLabel} sm={3}>Bezugsebene</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objektMeta.bezugsebene} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  <option value="Mitte Druckanschluss">Mitte Druckanschluss</option>
                  <option value="Zeigerachse">Zeigerachse</option>
                  <option value="Dichtfläche Druckanschluss">Dichtfläche Druckanschluss°</option>
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup controlId="bemerkung">
            <Col sm={3} componentClass={ControlLabel}>Bemerkung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Bemerkung" id="bemerkung" value={this.objekt.bemerkung} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          {/* Die Funktion zur Übernahme der ÜO Messmitteldaten wird modal angezeigt */}
          <ObjektMessmittelUebernehmenModal kategorie="bps" onUebernehme={this.uebernehmeMessmittelDaten} show={this.modalVisible} onHideModal={this.hideModal}/>

        </Panel>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/objekte/ObjektAnlegenDMG.tsx